import { useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';

import Button from '@/V2/components/ui/button/button';
import { FormField } from '@/V2/components/ui/form/form-field';
import { TextArea } from '@/V2/components/ui/form/textarea';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { useAddProduct } from '../../hooks/use-add-product';
import { useGetValueProposition } from '../../hooks/use-brand-strategy';
import { useDeleteProduct } from '../../hooks/use-delete-product';
import { useUpdateProduct } from '../../hooks/use-update-product';
import { updateBrandStrategy } from '../../services';
import { useBrandStrategyStore } from '../../store/brand-strategy-store';
import { BrandStrategy, Competitor, CreateProduct, Product } from '../../types';
import { FormGroup } from '../ui/form-group';

const productFormSchema = z.object({
  id: z.string().nullable(),
  name: z.string().min(1, 'Product name is required'),
  usage_description: z.string().optional(),
  market_gap: z.string().optional(),
  competitive_difference: z.string().optional(),
  price_point: z.string().optional(),
});

const valuePropositionSchema = z.object({
  why_customers_choose_you: z.string().optional(),
  why_customers_do_not_choose_you: z.string().optional(),
  products: z.array(productFormSchema),
  competitors: z.string().optional(),
});

type ValuePropositionFormValues = z.infer<typeof valuePropositionSchema>;

interface ValuePropositionFormProps {
  projectId: string;
}

export const ValuePropositionForm = ({
  projectId,
}: ValuePropositionFormProps) => {
  const { brandStrategyId, brandStrategy } = useBrandStrategyStore();
  const queryClient = useQueryClient();
  const { data: valuePropositionData } = useGetValueProposition(projectId);
  const addProductMutation = useAddProduct(projectId);
  const updateProductMutation = useUpdateProduct(projectId);
  const deleteProductMutation = useDeleteProduct(projectId);
  const showField = false;
  const [productChanges, setProductChanges] = useState<
    Record<string, Partial<Product>>
  >({});

  // Track which product forms should be expanded
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(
    new Set()
  );

  // Create stable form data that only changes when value proposition data actually changes
  const stableFormData = useMemo(() => {
    if (valuePropositionData?.data) {
      const competitors = Array.isArray(valuePropositionData.data.competitors)
        ? valuePropositionData.data.competitors.map((c) => c.name).join('\n')
        : '';

      const products = valuePropositionData.data.products?.length
        ? valuePropositionData.data.products.map((p) => ({
            id: p.id,
            name: p.name,
            usage_description: p.usage_description || '',
            market_gap: p.market_gap || '',
            competitive_difference: p.competitive_difference || '',
            price_point: p.price_point || '',
          }))
        : [
            {
              id: null,
              name: '',
              usage_description: '',
              market_gap: '',
              competitive_difference: '',
              price_point: '',
            },
          ];

      return {
        why_customers_choose_you:
          valuePropositionData.data.why_customers_choose_you || '',
        why_customers_do_not_choose_you:
          valuePropositionData.data.why_customers_do_not_choose_you || '',
        products,
        competitors,
      };
    }

    // Default form data
    return {
      why_customers_choose_you: '',
      why_customers_do_not_choose_you: '',
      products: [
        {
          id: null,
          name: '',
          usage_description: '',
          market_gap: '',
          competitive_difference: '',
          price_point: '',
        },
      ],
      competitors: '',
    };
  }, [valuePropositionData?.data]);

  const form = useForm<ValuePropositionFormValues>({
    resolver: zodResolver(valuePropositionSchema),
    defaultValues: stableFormData,
  });

  // Track previous data to handle form synchronization
  const prevDataRef = useRef(stableFormData);

  // Handle form resets when data changes
  if (JSON.stringify(prevDataRef.current) !== JSON.stringify(stableFormData)) {
    form.reset(stableFormData);
    prevDataRef.current = stableFormData;
  }

  const { mutate: updateStrategy, isPending } = useMutation({
    mutationFn: async (data: ValuePropositionFormValues) => {
      if (!brandStrategyId || !brandStrategy) {
        throw new Error('Brand strategy data is required');
      }

      // Convert competitors string to array
      const competitors: Competitor[] = (data.competitors || '')
        .split('\n')
        .filter(Boolean)
        .map((name) => ({
          id: crypto.randomUUID(),
          name: name.trim(),
          products: '',
        }));

      // update the value proposition fields
      const updatedStrategy: Partial<BrandStrategy> = {
        id: brandStrategy.id,
        why_customers_choose_you: data.why_customers_choose_you,
        why_customers_do_not_choose_you: data.why_customers_do_not_choose_you,
        competitors,
      };

      const response = await updateBrandStrategy(
        projectId,
        brandStrategyId,
        updatedStrategy as BrandStrategy
      );
      return response.data;
    },
    onSuccess: (data) => {
      // Update the store with the new data
      useBrandStrategyStore.getState().setBrandStrategy(data);

      // Update the form with the new data using stable approach
      const currentFormValues = form.getValues();
      const updatedFormData = {
        why_customers_choose_you: data.why_customers_choose_you || '',
        why_customers_do_not_choose_you:
          data.why_customers_do_not_choose_you || '',
        products: data.products?.length
          ? data.products.map((p) => ({
              id: p.id,
              name: p.name,
              usage_description: p.usage_description || '',
              market_gap: p.market_gap || '',
              competitive_difference: p.competitive_difference || '',
              price_point: p.price_point || '',
            }))
          : [
              {
                id: null,
                name: '',
                usage_description: '',
                market_gap: '',
                competitive_difference: '',
                price_point: '',
              },
            ],
        competitors: Array.isArray(data.competitors)
          ? data.competitors.map((c) => c.name).join('\n')
          : '',
      };

      // Only reset if data has actually changed
      const hasFormDataChanged = () => {
        if (
          currentFormValues.why_customers_choose_you !==
          updatedFormData.why_customers_choose_you
        ) {
          return true;
        }

        if (
          currentFormValues.why_customers_do_not_choose_you !==
          updatedFormData.why_customers_do_not_choose_you
        ) {
          return true;
        }

        if (currentFormValues.competitors !== updatedFormData.competitors) {
          return true;
        }

        if (
          currentFormValues.products.length !== updatedFormData.products.length
        ) {
          return true;
        }

        return updatedFormData.products.some((product, index) => {
          const currentProduct = currentFormValues.products[index];
          return (
            !currentProduct ||
            currentProduct.id !== product.id ||
            currentProduct.name !== product.name ||
            currentProduct.usage_description !== product.usage_description ||
            currentProduct.market_gap !== product.market_gap ||
            currentProduct.competitive_difference !==
              product.competitive_difference ||
            currentProduct.price_point !== product.price_point
          );
        });
      };

      if (hasFormDataChanged()) {
        form.reset(updatedFormData);
      }

      // Invalidate both queries to ensure fresh data
      queryClient.invalidateQueries({
        queryKey: ['value-proposition', projectId],
      });
      queryClient.invalidateQueries({
        queryKey: ['brandStrategy', projectId],
      });
    },
    onError: (error) => {
      console.error('Error updating value proposition:', error);
      toast.error('Failed to update value proposition');
    },
  });

  const handleSubmit = form.handleSubmit(
    async (data: ValuePropositionFormValues) => {
      try {
        const productUpdatePromises = [
          // Save product changes
          ...Object.entries(productChanges).map(([productId, changes]) => {
            if (!changes) return null;

            // Clean the data by removing empty values
            const cleanedChanges = Object.fromEntries(
              Object.entries(changes).filter(
                ([, value]) =>
                  value !== '' && value !== null && value !== undefined
              )
            );

            if (productId.startsWith('new_')) {
              // Create new product
              const newProduct: CreateProduct = {
                name: cleanedChanges.name || '',
                usage_description:
                  cleanedChanges.usage_description || undefined,
                market_gap: cleanedChanges.market_gap || undefined,
                competitive_difference:
                  cleanedChanges.competitive_difference || undefined,
                price_point: cleanedChanges.price_point || undefined,
              };

              // Filter out empty fields from the new product
              const cleanedProduct = Object.fromEntries(
                Object.entries(newProduct).filter(([, value]) => value !== '')
              ) as CreateProduct;

              return addProductMutation
                .mutateAsync(cleanedProduct)
                .then((result) => {
                  // Update expanded products when new product gets an ID
                  setExpandedProducts((prev) => {
                    const newSet = new Set(prev);
                    newSet.delete(productId); // Remove the temporary key
                    if (result?.id) {
                      newSet.add(result.id); // Add the real ID
                    }
                    return newSet;
                  });
                  return result;
                });
            } else {
              // For existing products, get the current product data
              const currentProduct = form
                .getValues()
                .products.find((p) => p.id === productId);
              if (!currentProduct) return null;

              // Update existing product
              return updateProductMutation.mutateAsync({
                id: productId,
                name: currentProduct.name,
                ...cleanedChanges,
              } as Product);
            }
          }),
        ];

        await Promise.all(productUpdatePromises.filter(Boolean));
        setProductChanges({});

        // save the value proposition changes
        await updateStrategy(data);

        // Invalidate queries to refresh data
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ['value-proposition', projectId],
          }),
          queryClient.invalidateQueries({
            queryKey: ['brandStrategy', projectId],
          }),
        ]);

        // Refetch the data to ensure we have the latest state
        await Promise.all([
          queryClient.refetchQueries({
            queryKey: ['value-proposition', projectId],
          }),
          queryClient.refetchQueries({
            queryKey: ['brandStrategy', projectId],
          }),
        ]);

        toast.success('All changes saved successfully');
      } catch (error) {
        console.error('Error saving changes:', error);
        toast.error('Failed to save some changes');
      }
    }
  );

  const { fields, append, remove } = useFieldArray({
    name: 'products',
    control: form.control,
  });

  const handleProductChange = (
    productId: string | null,
    changes: Partial<Product>,
    index: number
  ) => {
    setProductChanges((prev) => ({
      ...prev,
      [productId || `new_${index}`]: {
        ...prev[productId || `new_${index}`],
        ...changes,
      },
    }));
  };

  const handleAddProduct = () => {
    const newIndex = fields.length;
    append({
      id: null,
      name: '',
      usage_description: '',
      market_gap: '',
      competitive_difference: '',
      price_point: '',
    });

    // Expand the newly added product form
    setExpandedProducts((prev) => new Set([...prev, `new_${newIndex}`]));
  };

  const handleProductDelete = async (index: number) => {
    const product = form.getValues().products[index];

    if (!product.id) {
      // If no id, it's a new entry that hasn't been saved yet
      remove(index);
      setProductChanges((prev) => {
        const newChanges = { ...prev };
        delete newChanges[`new_${index}`];
        return newChanges;
      });

      // Remove from expanded products
      setExpandedProducts((prev) => {
        const newSet = new Set(prev);
        newSet.delete(`new_${index}`);
        return newSet;
      });
      return;
    }

    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await deleteProductMutation.mutateAsync(product.id);
        remove(index);
        setProductChanges((prev) => {
          const newChanges = { ...prev };
          delete newChanges[product.id!];
          return newChanges;
        });

        // Remove from expanded products
        setExpandedProducts((prev) => {
          const newSet = new Set(prev);
          newSet.delete(product.id!);
          return newSet;
        });
      } catch (error) {
        console.error('Error deleting product:', error);
        toast.error('Failed to delete product');
      }
    }
  };

  return (
    <div>
      <div className='flex justify-between items-center mb-6'>
        <div className='flex flex-col'>
          <h2 className='text-xl font-semibold mb-1'>Value Proposition</h2>
          <p className='text-sm'>Products or Services</p>
        </div>
        <div className='flex mt-2 mr-4'>
          <Button
            type='submit'
            variant='primary'
            size='xsmall'
            onClick={handleSubmit}
            isLoading={
              isPending ||
              addProductMutation.isPending ||
              updateProductMutation.isPending
            }
          >
            Save
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className='space-y-8 mr-4'>
        <div className='space-y-6'>
          {fields.map((field, index: number) => {
            const product = form.getValues().products[index];
            const productKey = product.id || `new_${index}`;
            const isExpanded = expandedProducts.has(productKey) || index === 0;

            return (
              <FormGroup
                key={field.id}
                title={
                  !product.id
                    ? 'Adding New Product'
                    : form.getValues(`products.${index}.name`) ||
                      `Product ${index + 1}`
                }
                defaultCollapsed={!isExpanded}
              >
                <div className='space-y-4'>
                  {!product.id && (
                    <div className='text-sm font-medium text-gray-600 mb-2 italic'>
                      Adding New Product
                    </div>
                  )}
                  <FormField
                    label='What product or service do you offer?'
                    required
                    error={
                      form.formState.errors.products?.[index]?.name?.message
                    }
                  >
                    <Controller
                      name={`products.${index}.name`}
                      control={form.control}
                      render={({ field }) => (
                        <TextArea
                          placeholder='Enter product name...'
                          className='min-h-[60px]'
                          {...field}
                          onChange={(e) => {
                            field.onChange(e);
                            const product = form.getValues().products[index];
                            handleProductChange(
                              product.id,
                              { name: e.target.value },
                              index
                            );
                          }}
                        />
                      )}
                    />
                  </FormField>

                  <FormField
                    label='How does the customer use your product or service work?'
                    error={
                      form.formState.errors.products?.[index]?.usage_description
                        ?.message
                    }
                  >
                    <Controller
                      name={`products.${index}.usage_description`}
                      control={form.control}
                      render={({ field }) => (
                        <TextArea
                          placeholder='Describe how the product is used...'
                          className='min-h-[100px]'
                          {...field}
                          onChange={(e) => {
                            field.onChange(e);
                            const product = form.getValues().products[index];
                            handleProductChange(
                              product.id,
                              { usage_description: e.target.value },
                              index
                            );
                          }}
                        />
                      )}
                    />
                  </FormField>

                  <FormField
                    label='What product(s) are you trying to replace or what gap are you trying to fill?'
                    error={
                      form.formState.errors.products?.[index]?.market_gap
                        ?.message
                    }
                  >
                    <Controller
                      name={`products.${index}.market_gap`}
                      control={form.control}
                      render={({ field }) => (
                        <TextArea
                          placeholder='Describe the market gap this product fills...'
                          className='min-h-[100px]'
                          {...field}
                          onChange={(e) => {
                            field.onChange(e);
                            const product = form.getValues().products[index];
                            handleProductChange(
                              product.id,
                              { market_gap: e.target.value },
                              index
                            );
                          }}
                        />
                      )}
                    />
                  </FormField>

                  <FormField
                    label='How is your product or service different from the competitors?'
                    error={
                      form.formState.errors.products?.[index]
                        ?.competitive_difference?.message
                    }
                  >
                    <Controller
                      name={`products.${index}.competitive_difference`}
                      control={form.control}
                      render={({ field }) => (
                        <TextArea
                          placeholder='What makes this product different from competitors...'
                          className='min-h-[100px]'
                          {...field}
                          onChange={(e) => {
                            field.onChange(e);
                            const product = form.getValues().products[index];
                            handleProductChange(
                              product.id,
                              { competitive_difference: e.target.value },
                              index
                            );
                          }}
                        />
                      )}
                    />
                  </FormField>

                  <FormField
                    label='What is the price point for your product?'
                    error={
                      form.formState.errors.products?.[index]?.price_point
                        ?.message
                    }
                  >
                    <Controller
                      name={`products.${index}.price_point`}
                      control={form.control}
                      render={({ field }) => (
                        <TextArea
                          placeholder='Price range or "high" / "low" description of price...'
                          className='min-h-[100px]'
                          {...field}
                          onChange={(e) => {
                            field.onChange(e);
                            const product = form.getValues().products[index];
                            handleProductChange(
                              product.id,
                              { price_point: e.target.value },
                              index
                            );
                          }}
                        />
                      )}
                    />
                  </FormField>

                  <div className='flex justify-end'>
                    <button
                      type='button'
                      onClick={() => handleProductDelete(index)}
                      className='text-sm underline text-[#345FF5] hover:text-red-700 hover:underline'
                    >
                      Delete Product
                    </button>
                  </div>
                </div>
              </FormGroup>
            );
          })}
        </div>
        <div className='flex justify-start'>
          <Button
            type='button'
            variant='secondary'
            size='xsmall'
            onClick={handleAddProduct}
          >
            Add Another Product
          </Button>
        </div>
        <FormField
          label='Why do customers choose you over other products and services?'
          error={form.formState.errors.why_customers_choose_you?.message}
        >
          <Controller
            name='why_customers_choose_you'
            control={form.control}
            render={({ field }) => (
              <TextArea
                placeholder='If you can succeed then...'
                className='min-h-[100px]'
                {...field}
              />
            )}
          />
        </FormField>

        <FormField
          label="Why prospective customers shouldn't use you?"
          error={form.formState.errors.why_customers_do_not_choose_you?.message}
        >
          <Controller
            name='why_customers_do_not_choose_you'
            control={form.control}
            render={({ field }) => (
              <TextArea
                placeholder='Why would customers choose another product or company other than yours?'
                className='min-h-[100px]'
                {...field}
              />
            )}
          />
        </FormField>

        {showField && (
          <FormField
            label='Competitors'
            required
            error={form.formState.errors.competitors?.message}
          >
            <Controller
              name='competitors'
              control={form.control}
              render={({ field }) => (
                <TextArea
                  placeholder='List your main competitors (one per line)...'
                  className='min-h-[100px]'
                  {...field}
                />
              )}
            />
          </FormField>
        )}
      </form>
    </div>
  );
};
